import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { SEOHead } from '@/components/seo/SEOHead';
import { Check, Zap } from 'lucide-react';

export const PricingPage = () => {
  const { t } = useTranslation('landing');

  const pricingTiers = [
    {
      name: 'Free',
      price: '€0',
      duration: '36 hours',
      description: 'Get started tracking',
      features: [
        "Today's data access",
        '36-hour history',
        'Basic tracking',
      ],
      cta: 'Try Free',
      mostPopular: false,
      ctaLink: 'https://app2.peetrack.live/login',
    },
    {
      name: 'One-Time Pass',
      price: '€2.49',
      duration: '5 days access',
      description: 'Perfect for testing',
      features: [
        'Voice input',
        'Doctor sharing',
        'Full export',
        '5-day history',
      ],
      cta: 'Buy Now',
      mostPopular: false,
      ctaLink: 'https://app2.peetrack.live/login',
    },
    {
      name: 'Pro',
      price: '€3.99',
      duration: '/month',
      yearlyPrice: '€39.99/year',
      description: 'Full tracking power',
      features: [
        'Unlimited history',
        'Unlimited tasks',
        'Advanced reminders',
        'Priority support',
      ],
      cta: 'Subscribe',
      mostPopular: true,
      ctaLink: 'https://app2.peetrack.live/login',
    },
    {
      name: 'Premium',
      price: '€19.99',
      duration: '/month',
      yearlyPrice: '€149.99/year',
      description: 'AI-powered insights',
      features: [
        'Pro features + more',
        'AI analysis',
        'AI assistant',
        'Predictive insights',
      ],
      cta: 'Get Premium',
      mostPopular: false,
      ctaLink: 'https://app2.peetrack.live/login',
    },
  ];

  return (
    <>
      <SEOHead
        title="Pricing Plans - PeeTrack"
        description="Choose the perfect plan for your urinary wellness tracking needs. From free basic tracking to AI-powered premium insights."
        keywords={[
          'pricing',
          'subscription plans',
          'health tracking pricing',
          'medical app pricing',
        ]}
        canonical="/pricing"
      />

      <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-white dark:from-gray-900 dark:to-gray-800">
        {/* Header Section */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4 max-w-7xl">
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
                {t('pricing.title', 'Pricing Plans')}
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                {t('pricing.subtitle', 'Choose the perfect plan for your urinary wellness tracking needs')}
              </p>
            </div>

            {/* Pricing Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
              {pricingTiers.map((tier, index) => (
                <div
                  key={index}
                  className={`relative rounded-2xl bg-white dark:bg-gray-800 p-8 shadow-xl transition-all hover:scale-105 ${
                    tier.mostPopular ? 'ring-4 ring-cyan-500' : ''
                  }`}
                >
                  {tier.mostPopular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-cyan-500 to-cyan-600 px-4 py-1 text-sm font-bold text-white shadow-lg">
                      <Zap className="h-4 w-4" />
                      Most Popular
                    </div>
                  )}

                  <div className="mb-6">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{tier.name}</h3>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{tier.description}</p>
                  </div>

                  <div className="mb-6">
                    <div className="flex items-baseline gap-2">
                      <span className="text-5xl font-bold text-gray-900 dark:text-white">{tier.price}</span>
                      <span className="text-gray-600 dark:text-gray-400">{tier.duration}</span>
                    </div>
                    {tier.yearlyPrice && (
                      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                        or {tier.yearlyPrice} (save 17%)
                      </p>
                    )}
                  </div>

                  <ul className="mb-8 space-y-3">
                    {tier.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start gap-3">
                        <Check className="h-5 w-5 flex-shrink-0 text-cyan-500 mt-0.5" />
                        <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <a
                    href={tier.ctaLink}
                    className={`block w-full rounded-full py-3 text-center font-bold transition-all ${
                      tier.mostPopular
                        ? 'bg-gradient-to-r from-cyan-500 to-cyan-600 text-white hover:from-cyan-600 hover:to-cyan-700 shadow-lg hover:shadow-xl'
                        : 'border-2 border-cyan-500 text-cyan-600 dark:text-cyan-400 hover:bg-cyan-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {tier.cta}
                  </a>
                </div>
              ))}
            </div>

            {/* FAQ or Additional Info */}
            <div className="mt-16 max-w-3xl mx-auto text-center">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                All plans include basic tracking, mobile apps (iOS & Android), and 8 languages.
              </p>
              <p className="text-gray-600 dark:text-gray-400">
                Questions? <Link to="/privacy" className="text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 font-semibold">Contact us</Link> or check our{' '}
                <Link to="/privacy" className="text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 font-semibold">privacy policy</Link>.
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};
